import { useRef, useEffect, useState, useCallback } from 'react'
import WaveSurfer from 'wavesurfer.js'

/* --- Constants ---------------------------------------------------------- */

const SPEED_OPTIONS = [1, 1.5, 2] as const
export type SpeedOption = (typeof SPEED_OPTIONS)[number]
export { SPEED_OPTIONS }

/* --- Helpers ------------------------------------------------------------ */

// Wave canvas dimensions
const WAVE_TOP_H = 40   // interactive bars
const WAVE_BOT_H = 16   // reflection
const WAVE_TOTAL_H = WAVE_TOP_H + 1 + WAVE_BOT_H  // +1 for white divider line

export function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Builds the waveGradient and progressGradient on a temporary canvas. */
function buildWaveGradients() {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.height = WAVE_TOTAL_H
  canvas.width = 1  // width does not matter for a vertical gradient

  const splitRatio = WAVE_TOP_H / WAVE_TOTAL_H
  const lineStart = splitRatio
  const lineEnd = (WAVE_TOP_H + 1) / WAVE_TOTAL_H

  const addStops = (g: CanvasGradient, top: string, bot: string) => {
    g.addColorStop(0, top)
    g.addColorStop(splitRatio, top)
    g.addColorStop(lineStart, '#e8ecf0')  // soft white divider
    g.addColorStop(lineEnd, '#e8ecf0')
    g.addColorStop(lineEnd, bot)
    g.addColorStop(1, bot)
  }

  const mk = () => ctx.createLinearGradient(0, 0, 0, WAVE_TOTAL_H)

  const waveGradient = mk()
  addStops(waveGradient, '#b5c0cc', '#d7dde4')  // gray top / light-gray reflection

  const progressGradient = mk()
  addStops(progressGradient, '#0068f5', '#d7dde4')  // blue played / reflection stays gray

  return { waveGradient, progressGradient }
}

/* --- Custom hook -------------------------------------------------------- */

interface UseAudioPlayerOptions {
  audioUrl: string
  playerId: string
  onPlay?: (id: string) => void
  isActive: boolean
}

export function useAudioPlayer({ audioUrl, playerId, onPlay, isActive }: UseAudioPlayerOptions) {
  const waveformRef = useRef<HTMLDivElement>(null)
  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const hoverRef = useRef<HTMLDivElement>(null)
  const timeRef = useRef<HTMLDivElement>(null)
  const playheadRatio = useRef(0)
  const lastPointerX = useRef<number | null>(null)
  const speedRef = useRef<SpeedOption>(1)
  // Stable ref so WaveSurfer closures always call the latest onPlay
  const onPlayRef = useRef(onPlay)
  useEffect(() => { onPlayRef.current = onPlay }, [onPlay])

  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState('0:00')
  const [isReady, setIsReady] = useState(false)
  const [speed, setSpeed] = useState<SpeedOption>(1)

  /* -- Initialise WaveSurfer -- */
  useEffect(() => {
    if (!waveformRef.current) return

    const { waveGradient, progressGradient } = buildWaveGradients()
     
    // Pre-create the <audio> element with the direct URL as src.
    // WaveSurfer's setSrc() skips blob creation when media.src already
    // equals the requested URL, avoiding blob: URLs that violate CSP.
    const audioEl = document.createElement('audio')
    audioEl.src = audioUrl
    audioEl.preload = 'metadata'

    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: waveGradient,
      progressColor: progressGradient,
      cursorColor: 'transparent',
      barWidth: 3,
      barGap: 2,
      barRadius: 2,
      height: WAVE_TOTAL_H,
      normalize: true,
      interact: true,
      media: audioEl,
    })

    ws.on('ready', () => { setIsReady(true); ws.setPlaybackRate(speedRef.current) })
    ws.on('play', () => { setIsPlaying(true); onPlayRef.current?.(playerId) })
    ws.on('pause', () => setIsPlaying(false))
    ws.on('finish', () => setIsPlaying(false))
    ws.on('decode', (d: number) => setDuration(formatTime(d)))

    const container = waveformRef.current!

    /** Positions the hover gap div between the playhead and cursorX. */
    const updateGap = (cursorX: number) => {
      if (!hoverRef.current) return
      const cursorRatio = cursorX / container.offsetWidth
      const gapStart = Math.min(cursorRatio, playheadRatio.current)
      const gapEnd = Math.max(cursorRatio, playheadRatio.current)
      hoverRef.current.style.left = `${gapStart * 100}%`
      hoverRef.current.style.width = `${(gapEnd - gapStart) * 100}%`
    }

    ws.on('timeupdate', (time: number) => {
      const dur = ws.getDuration()
      playheadRatio.current = dur > 0 ? time / dur : 0
      if (lastPointerX.current === null) {
        if (timeRef.current) timeRef.current.textContent = formatTime(time)
      } else {
        updateGap(lastPointerX.current)
      }
    })

    const onPointerMove = (e: PointerEvent) => {
      lastPointerX.current = e.offsetX
      updateGap(e.offsetX)
      if (timeRef.current) {
        timeRef.current.textContent = formatTime((e.offsetX / container.offsetWidth) * ws.getDuration())
      }
    }

    const onPointerLeave = () => {
      lastPointerX.current = null
      if (hoverRef.current) hoverRef.current.style.width = '0'
      if (timeRef.current) timeRef.current.textContent = formatTime(ws.getCurrentTime())
    }

    container.addEventListener('pointermove', onPointerMove)
    container.addEventListener('pointerleave', onPointerLeave)

    wavesurferRef.current = ws

    return () => {
      container.removeEventListener('pointermove', onPointerMove)
      container.removeEventListener('pointerleave', onPointerLeave)
      ws.destroy()
      wavesurferRef.current = null
    }
  }, [audioUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  /* -- Re-load when audioUrl changes -- */
  useEffect(() => {
    const ws = wavesurferRef.current
    if (!ws) return
    setIsReady(false)
    setIsPlaying(false)
    setDuration('0:00')
    if (timeRef.current) timeRef.current.textContent = '0:00'
    void ws.load(audioUrl)
  }, [audioUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  /* -- Pause when another player becomes active -- */
  useEffect(() => {
    if (!isActive) wavesurferRef.current?.pause()
  }, [isActive])

  const handlePlayPause = useCallback(() => {
    wavesurferRef.current?.playPause()
  }, [])

  const handleSpeedSelect = useCallback((next: SpeedOption) => {
    speedRef.current = next
    setSpeed(next)
    wavesurferRef.current?.setPlaybackRate(next)
  }, [])

  return { waveformRef, hoverRef, timeRef, isPlaying, duration, isReady, handlePlayPause, speed, handleSpeedSelect }
}
